import { createAsyncThunk } from "@reduxjs/toolkit";
import { environment } from "../../environments";
import {
  CreateInvitationRequest,
  CreateInvitationResponse,
  GetDigitalInvitationDetailsRequest,
  GetDigitalInvitationDetailsResponse,
  GetEventTypesRequest,
  GetEventTypesResponse,
  SaveDigitalInvitationKeysRequest,
  SaveDigitalInvitationKeysResponse,
  SendDigitalInvitationSMSRequest,
  SendDigitalInvitationSMSResponse,
} from "../../models/DigitalOrder.model";

const API_BASE_URL = environment.apiUrl;

export const getEventTypes = createAsyncThunk<
  GetEventTypesResponse,
  GetEventTypesRequest,
  { rejectValue: string }
>(
  "digitalOrder/GetEventTypes",
  async (requestData, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}DigitalOrder/GetEventTypes`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestData),
        },
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data: GetEventTypesResponse = await response.json();

      if (!data.success)
        return rejectWithValue(data.message || "Failed to fetch event types");
      return data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error",
      );
    }
  },
);

export const createInvitation = createAsyncThunk<
  CreateInvitationResponse,
  CreateInvitationRequest,
  { rejectValue: string }
>(
  "digitalOrder/CreateInvitation",
  async (requestData, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}DigitalOrder/CreateInvitation`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestData),
        },
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data: CreateInvitationResponse = await response.json();

      if (!data.success)
        return rejectWithValue(
          data.message || "Failed to create invitation",
        );
      return data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error",
      );
    }
  },
);

export const getDigitalInvitationDetails = createAsyncThunk<
  GetDigitalInvitationDetailsResponse,
  GetDigitalInvitationDetailsRequest,
  { rejectValue: string }
>(
  "digitalOrder/GetDigitalInvitationDetails",
  async (requestData) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}DigitalOrder/GetDigitalInvitationDetails`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestData),
        },
      );

      // Read text first — response.json() can throw on some error responses
      const text = await response.text();
      const data: GetDigitalInvitationDetailsResponse = text
        ? JSON.parse(text)
        : { success: false, message: "", digitalInvitationDetails: {} as GetDigitalInvitationDetailsResponse["digitalInvitationDetails"] };

      if (!response.ok) {
        // 400/500 — no invitation exists, or server error. Either way, open the creation form.
        return data.success ? data : { ...data, success: false };
      }

      return data;
    } catch (error) {
      // Network error — show the creation form, not an error message.
      return {
        success: false,
        message: "",
        digitalInvitationDetails: {} as GetDigitalInvitationDetailsResponse["digitalInvitationDetails"],
      };
    }
  },
);

export const sendDigitalInvitationSMS = createAsyncThunk<
  SendDigitalInvitationSMSResponse,
  SendDigitalInvitationSMSRequest,
  { rejectValue: string }
>(
  "digitalOrder/SendDigitalInvitationSMS",
  async (requestData, { rejectWithValue }) => {
    try {
      const response = await fetch(
        "https://vmi2962816.contaboserver.net/webhook/Mozmanim/ResendSMS",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            AccessKey: requestData.accessKey,
            EditKey: requestData.editKey,
            PhoneNumber: requestData.phoneNumber,
          }),
        },
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data: SendDigitalInvitationSMSResponse = await response.json();

      if (data.Status !== "success")
        return rejectWithValue(data.Code || "Failed to send SMS");
      return data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error",
      );
    }
  },
);

export const saveDigitalInvitationKeys = createAsyncThunk<
  SaveDigitalInvitationKeysResponse,
  SaveDigitalInvitationKeysRequest,
  { rejectValue: string }
>(
  "digitalOrder/SaveDigitalInvitationKeys",
  async (requestData, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}DigitalOrder/SaveDigitalInvitationKeys`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestData),
        },
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data: SaveDigitalInvitationKeysResponse = await response.json();

      return data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error",
      );
    }
  },
);
